/**
 * FeaturedCommunityRituals Component
 * Carousel section showing rituals from communities.
 * Title: uppercase section-heading, "Show more ->" link on right.
 * 2 cards per screen on desktop, 1 on mobile.
 */

import { memo } from "react"
import { Link } from "react-router-dom"
import { Carousel } from "@/components/carousel/Carousel"
import { useTranslation } from "@/language"
import type { CommunityRitual } from "../types/connect.types"
import { CommunityRitualCard } from "./CommunityRitualCard"

interface FeaturedCommunityRitualsProps {
  rituals: CommunityRitual[]
  onJoinCommunity?: (communityId: string) => void
}

export const FeaturedCommunityRituals = memo(function FeaturedCommunityRituals({
  rituals,
  onJoinCommunity,
}: FeaturedCommunityRitualsProps) {
  const { t } = useTranslation("common")
  if (rituals.length === 0) return null

  return (
    <section className="pb-10 md:py-12 max-w-360 mx-auto px-5 md:px-17.5">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 md:mb-6">
        <h2 className="text-xl font-semibold md:section-heading text-text-primary uppercase">
          {t("connect.featuredCommunityRituals")}
        </h2>
        <Link
          to="/connect"
          className="hidden md:block text-sm text-text-primary hover:opacity-70 transition-opacity whitespace-nowrap"
        >
          {t("connect.showMore")} &rarr;
        </Link>
      </div>

      {/* Carousel */}
      <Carousel
        data={rituals}
        renderItem={ritual => (
          <CommunityRitualCard ritual={ritual} onJoinCommunity={onJoinCommunity} />
        )}
        itemsPerScreen={{ sm: 1, md: 2, lg: 2, xl: 2 }}
        contentClassName="w-full"
      />
      <div className="mt-5 md:hidden ">
        <Link
          to="/connect"
          className="text-sm font-semibold text-text-primary hover:opacity-70 transition-opacity whitespace-nowrap"
        >
          {t("connect.showMore")} &rarr;
        </Link>
      </div>
    </section>
  )
})
